import * as React from "react";
import { SmartLink } from "./link-context";

export type Tone = "default" | "brand" | "success" | "warn" | "danger" | "mute";

// ---------------------------------------------------------------------------
// Button
// ---------------------------------------------------------------------------
export type ButtonVariant = "primary" | "brand" | "secondary" | "ghost";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  as?: "button" | "a";
  href?: string;
  leadingIcon?: React.ReactNode;
  trailingIcon?: React.ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  as = "button",
  className = "",
  leadingIcon,
  trailingIcon,
  children,
  href,
  ...rest
}: ButtonProps) {
  const cls = [
    "btn",
    `btn-${variant}`,
    size === "sm" && "btn-sm",
    size === "lg" && "btn-lg",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  if (as === "a") {
    return (
      <SmartLink
        className={cls}
        href={href || "#"}
        {...(rest as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
      >
        {leadingIcon}
        {children}
        {trailingIcon}
      </SmartLink>
    );
  }
  return (
    <button className={cls} {...rest}>
      {leadingIcon}
      {children}
      {trailingIcon}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Pill
// ---------------------------------------------------------------------------
export type PillTone = "default" | "scr" | "fail" | "ok" | "rep" | "warn";

export interface PillProps extends React.HTMLAttributes<HTMLSpanElement> {
  tone?: PillTone;
}

export function Pill({ tone = "default", className = "", children, ...rest }: PillProps) {
  const cls = ["pill", tone !== "default" && tone, className].filter(Boolean).join(" ");
  return (
    <span className={cls} {...rest}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Icon — wraps an SVG in a colored, rounded chip.
// ---------------------------------------------------------------------------
export type IconTone = "default" | "brand" | "success" | "warn" | "danger" | "mute";
export type IconSize = "sm" | "md" | "lg";

export interface IconProps {
  tone?: IconTone;
  size?: IconSize;
  className?: string;
  children: React.ReactNode;
  "aria-label"?: string;
}

export function Icon({
  tone = "default",
  size = "md",
  className = "",
  children,
  ...rest
}: IconProps) {
  const cls = ["hc-icon", tone !== "default" && tone, size !== "md" && `size-${size}`, className]
    .filter(Boolean)
    .join(" ");
  return (
    <span className={cls} {...rest}>
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// StatusDot
// ---------------------------------------------------------------------------
export interface StatusDotProps {
  tone?: "success" | "warn" | "danger";
  pulse?: boolean;
  className?: string;
}
export function StatusDot({ tone = "success", pulse = true, className = "" }: StatusDotProps) {
  const cls = ["status-dot", tone, pulse && "pulse", className].filter(Boolean).join(" ");
  return <span className={cls} />;
}

// ---------------------------------------------------------------------------
// StatusChip
// ---------------------------------------------------------------------------
export interface StatusChipProps {
  tone?: "success" | "warn" | "danger";
  pulse?: boolean;
  children: React.ReactNode;
  className?: string;
}
export function StatusChip({
  tone = "success",
  pulse = true,
  children,
  className = "",
}: StatusChipProps) {
  return (
    <span className={["status-chip", tone, className].filter(Boolean).join(" ")}>
      <StatusDot tone={tone} pulse={pulse} />
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// Kicker
// ---------------------------------------------------------------------------
export interface KickerProps {
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
}
export function Kicker({ icon, children, className = "" }: KickerProps) {
  return (
    <div className={["hero-kicker", className].filter(Boolean).join(" ")}>
      {icon}
      <span>{children}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card + CardHeader
// ---------------------------------------------------------------------------
export interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  wide?: boolean;
}
export function Card({ className = "", wide, children, ...rest }: CardProps) {
  return (
    <div className={["card", wide && "wide", className].filter(Boolean).join(" ")} {...rest}>
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: React.ReactNode;
  meta?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}
export function CardHeader({ title, meta, action, className = "" }: CardHeaderProps) {
  return (
    <div className={["card-hdr", className].filter(Boolean).join(" ")}>
      <h3>{title}</h3>
      {meta && <span className="meta">{meta}</span>}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avatar
// ---------------------------------------------------------------------------
export interface AvatarProps {
  initials: string;
  size?: number;
  className?: string;
  tone?: "brand" | "gray";
}
export function Avatar({ initials, size = 28, className = "", tone = "brand" }: AvatarProps) {
  return (
    <div
      className={["av", tone, className].filter(Boolean).join(" ")}
      style={{ width: size, height: size, fontSize: Math.max(10, Math.round(size * 0.4)) }}
    >
      {initials}
    </div>
  );
}

// ---------------------------------------------------------------------------
// ColorChip — UA dashboard color indicator
// ---------------------------------------------------------------------------
export type ChipColor = "red" | "blue" | "green" | "yellow" | "purple";

export interface ColorChipProps {
  color: ChipColor;
  label: string;
  mine?: boolean;
  className?: string;
}
export function ColorChip({ color, label, mine, className = "" }: ColorChipProps) {
  return (
    <span className={["color-chip", mine && "mine", className].filter(Boolean).join(" ")}>
      <span className={`sw ${color}`} />
      {label}
    </span>
  );
}

// ---------------------------------------------------------------------------
// MonoMeta — small mono-font meta block
// ---------------------------------------------------------------------------
export interface MonoMetaProps {
  primary?: React.ReactNode;
  secondary?: React.ReactNode;
  align?: "left" | "right";
  className?: string;
}
export function MonoMeta({ primary, secondary, align = "right", className = "" }: MonoMetaProps) {
  return (
    <div className={["mono-meta", `align-${align}`, className].filter(Boolean).join(" ")}>
      {primary != null && <b>{primary}</b>}
      {secondary != null && <span>{secondary}</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Eyebrow — small capitalized label for marketing pages
// ---------------------------------------------------------------------------
export interface EyebrowProps {
  dot?: boolean;
  brand?: boolean;
  children: React.ReactNode;
  className?: string;
}
export function Eyebrow({ dot, brand, children, className = "" }: EyebrowProps) {
  return (
    <span
      className={["eyebrow", brand && "brand", dot && "dot", className].filter(Boolean).join(" ")}
    >
      {dot && <span className="eyebrow-dot" />}
      {children}
    </span>
  );
}

// ---------------------------------------------------------------------------
// FormField
// ---------------------------------------------------------------------------
export interface FormFieldProps {
  label: string;
  htmlFor: string;
  helpText?: string;
  errorText?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
}
export function FormField({
  label,
  htmlFor,
  helpText,
  errorText,
  required,
  children,
  className = "",
}: FormFieldProps) {
  return (
    <div className={["ds-field", className].filter(Boolean).join(" ")}>
      <label htmlFor={htmlFor} data-required={required ? "true" : undefined}>
        {label}
      </label>
      {children}
      {errorText ? <p className="ds-error">{errorText}</p> : null}
      {!errorText && helpText ? <p className="ds-help">{helpText}</p> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// TextInput
// ---------------------------------------------------------------------------
export interface TextInputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  "data-invalid"?: boolean;
}
export function TextInput({
  className = "",
  "data-invalid": dataInvalid,
  ...props
}: TextInputProps) {
  return (
    <input
      className={["ds-input", className].filter(Boolean).join(" ")}
      data-invalid={dataInvalid || undefined}
      aria-invalid={dataInvalid || undefined}
      {...props}
    />
  );
}

// ---------------------------------------------------------------------------
// InlineAlert
// ---------------------------------------------------------------------------
export interface InlineAlertProps {
  tone: "success" | "danger" | "info" | "neutral";
  children: React.ReactNode;
  className?: string;
}
export function InlineAlert({ tone, children, className = "" }: InlineAlertProps) {
  return (
    <div
      className={["ds-alert", className].filter(Boolean).join(" ")}
      data-tone={tone}
      role={tone === "danger" ? "alert" : "status"}
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Spinner — see loading.tsx (CSS border spinner)
// Re-exported here for backward compat; canonical export is from loading.tsx.
// ---------------------------------------------------------------------------
export { Spinner, type SpinnerProps, type SpinnerSize } from "./loading";

// ---------------------------------------------------------------------------
// CheckboxField
// ---------------------------------------------------------------------------
export interface CheckboxFieldProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "type"
> {
  label: React.ReactNode;
}
export function CheckboxField({
  className = "",
  id,
  label,
  required,
  ...props
}: CheckboxFieldProps) {
  return (
    <label className={["ds-checkbox-field", className].filter(Boolean).join(" ")} htmlFor={id}>
      <input id={id} type="checkbox" required={required} {...props} />
      <span>
        {label}
        {required ? <span aria-hidden="true"> *</span> : null}
      </span>
    </label>
  );
}

// ---------------------------------------------------------------------------
// AuthShell — centered auth page layout with card
// ---------------------------------------------------------------------------
export interface AuthShellProps {
  title: string;
  subtitle?: string;
  children: React.ReactNode;
  className?: string;
}
export function AuthShell({ title, subtitle, children, className = "" }: AuthShellProps) {
  return (
    <div className="ds-auth-page">
      <div className={["ds-auth-shell", className].filter(Boolean).join(" ")}>
        <div className="ds-auth-header">
          <h1>{title}</h1>
          {subtitle && <p>{subtitle}</p>}
        </div>
        <div className="ds-auth-card">{children}</div>
      </div>
    </div>
  );
}
